/**
 * details.ts — the three intermediate-value tables (P1 US-114).
 *
 * Ported from oracle lines 895–1029: `tableVals`, `detailsLamine`, `detailsCreux`, `detailsBois`.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * THIS IS PRESENTATION, AND IT READS FROM `envelope.values`
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * `values` is the engine's raw `v` bag, passed through verbatim by the adapter — ~79 keys for
 * lamine, ~81 for creux, ~46 for bois. That is decision D9 (full detail retained) working as
 * designed, and it is why these tables can exist at all.
 *
 * The composed cells — `fmt(a, 3) + ' / ' + fmt(b, 3)` — are **presentation and stay here**. They
 * are not engineering: nothing below computes a value, it only chooses how many decimals to show
 * and which two numbers to put side by side. Pushing them into the envelope would mean the API
 * shipping pre-rendered strings, which is exactly the coupling the contract exists to avoid.
 *
 * A missing key renders as `-` through `fmt` rather than throwing, so a partial bag degrades into
 * a table with gaps instead of a blank panel.
 *
 * `test/render.test.ts` executes the ORACLE's own `detailsLamine`/`Creux`/`Bois` against a golden
 * fixture and compares the HTML byte for byte with what this file produces. That is what makes the
 * "ported verbatim" claim checkable rather than asserted — and it is the reason these functions
 * keep the oracle's row order, symbols, units and decimal counts exactly.
 */

import { fmt } from './format';

/** The engine's intermediate-value bag, as it arrives in the envelope. */
export type Values = Record<string, unknown>;

/**
 * One table row: `[label, symbol, value, unit, decimals?]`.
 *
 * `null` is a section separator — the oracle renders it as an empty full-width row, which is what
 * gives the table its visual grouping without headings.
 */
export type ValueRow = readonly [string, string, unknown, string, number?] | null;

/** Oracle lines 895–904. A number goes through `fmt`; anything else is printed as-is. */
export function tableVals(rows: readonly ValueRow[]): string {
  let h = '<table class="vals"><tbody>';

  for (const p of rows) {
    if (p === null) {
      h += '<tr class="sep"><td colspan="4"></td></tr>';
      continue;
    }
    const value = typeof p[2] === 'number' ? fmt(p[2], p[4] === undefined ? 2 : p[4]) : p[2];
    h +=
      `<tr><td>${p[0]}</td><td class="sym">${p[1] || ''}</td><td class="num">` +
      `${String(value)}</td><td class="u">${p[3] || ''}</td></tr>`;
  }

  return `${h}</tbody></table>`;
}

/** Rolled profiles. Oracle lines 906–944: 34 rows and 4 separators. */
export function detailsLamine(v: Values): string {
  return tableVals([
    ['Hauteur / largeur', 'h / b', `${String(v['h'])} / ${String(v['b'])}`, 'mm'],
    ['Epaisseurs ame / semelle', 'tw / tf', `${String(v['tw'])} / ${String(v['tf'])}`, 'mm'],
    ['Aire de section', 'A', v['A'], 'mm2', 0],
    ['Inertie axe fort', 'Iy', v['Iy'], 'mm4', 0],
    ['Inertie axe faible', 'Iz', v['Iz'], 'mm4', 0],
    ['Module retenu axe fort', 'Wy', v['Wy'], 'mm3', 0],
    ['Module retenu axe faible', 'Wz', v['Wz'], 'mm3', 0],
    ['Masse lineique', 'G', v['G'], 'kg/m'],
    null,
    ["Limite d'elasticite", 'fy', v['fy'], 'MPa', 0],
    ['Coefficient epsilon', 'epsilon', v['eps'], '-', 3],
    ['Elancement de semelle', 'c/tf', v['ctf'], '-'],
    ["Elancement d'ame", 'c/tw', v['ctw'], '-'],
    ['CLASSE DE LA SECTION', 'Classe', v['classe'], '-', 0],
    null,
    ['Resistance plastique', 'Npl,Rd', v['NplRd'], 'kN'],
    ['Moment resistant axe fort', 'Mc,y,Rd', v['McyRd'], 'kN.m'],
    ['Moment resistant axe faible', 'Mc,z,Rd', v['MczRd'], 'kN.m'],
    ['Aire de cisaillement', 'Av', v['Av'], 'mm2', 0],
    ['Resistance au cisaillement', 'Vpl,Rd', v['VplRd'], 'kN'],
    ['Reduction par cisaillement', 'rho', v['rho'], '-', 3],
    ['Moment resistant reduit', 'Mv,y,Rd', v['MvyRd'], 'kN.m'],
    ['Voilement par cisaillement', 'hw/tw', v['voilement'], ''],
    null,
    ['Charge critique axe fort', 'Ncr,y', v['Ncry'], 'kN'],
    ['Charge critique axe faible', 'Ncr,z', v['Ncrz'], 'kN'],
    ['Elancement reduit y / z', 'lambda,bar', `${fmt(v['lamy'], 3)} / ${fmt(v['lamz'], 3)}`, '-'],
    ['Courbe de flambement y / z', '-', `${String(v['courbeY'])} / ${String(v['courbeZ'])}`, '-'],
    ['Coefficient chi,y / chi,z', 'chi', `${fmt(v['chiY'], 3)} / ${fmt(v['chiZ'], 3)}`, '-'],
    ['Resistance flambement', 'Nb,y,Rd', v['NbyRd'], 'kN'],
    ['Resistance flambement', 'Nb,z,Rd', v['NbzRd'], 'kN'],
    null,
    ['Moment critique de deversement', 'Mcr', v['Mcr'], 'kN.m'],
    ['Elancement reduit', 'lambda,bar,LT', v['lamLT'], '-', 3],
    ['Coefficient de deversement', 'chi,LT,mod', v['chiLTmod'], '-', 3],
    ['Moment resistant au deversement', 'Mb,Rd', v['MbRd'], 'kN.m'],
    ["Facteurs d'interaction", 'kyy / kzz', `${fmt(v['kyy'], 3)} / ${fmt(v['kzz'], 3)}`, '-'],
    ["Facteurs d'interaction", 'kyz / kzy', `${fmt(v['kyz'], 3)} / ${fmt(v['kzy'], 3)}`, '-'],
  ]);
}

/** Hollow sections. Oracle lines 946–993: 35 rows and 5 separators. */
export function detailsCreux(v: Values): string {
  return tableVals([
    [
      'Dimensions exterieures',
      'h x b x t',
      `${String(v['h'])} x ${String(v['b'])} x ${String(v['t'])}`,
      'mm',
    ],
    ['Rayon exterieur / interieur', 'ro / ri', `${fmt(v['ro'], 1)} / ${fmt(v['ri'], 1)}`, 'mm'],
    ['Aire de section', 'A', v['A'], 'mm2', 0],
    ['Inertie axe fort / faible', 'Iy / Iz', `${fmt(v['Iy'], 0)} / ${fmt(v['Iz'], 0)}`, 'mm4'],
    ['Module plastique y / z', 'Wpl', `${fmt(v['Wply'], 0)} / ${fmt(v['Wplz'], 0)}`, 'mm3'],
    ['Inertie de torsion', 'It', v['It'], 'mm4', 0],
    ['Masse lineique', 'G', v['G'], 'kg/m'],
    null,
    ["Limite d'elasticite", 'fy', v['fy'], 'MPa', 0],
    ['Elancement semelle / ame', 'c/t', `${fmt(v['ct'], 1)} / ${fmt(v['ctw'], 1)}`, '-'],
    ['CLASSE DE LA SECTION', 'Classe', v['classe'], '-', 0],
    null,
    ['Resistance plastique', 'Npl,Rd', v['NplRd'], 'kN'],
    ['Moment resistant axe fort', 'Mc,y,Rd', v['McyRd'], 'kN.m'],
    ['Moment resistant axe faible', 'Mc,z,Rd', v['MczRd'], 'kN.m'],
    ['Aire de cisaillement', 'Av,z', v['Av'], 'mm2', 0],
    ['Resistance au cisaillement', 'Vpl,Rd', v['VplRd'], 'kN'],
    ['Moment resistant reduit', 'Mv,y,Rd', v['MvyRd'], 'kN.m'],
    ["Exposant d'interaction", 'alpha = beta', v['expAB'], '-', 3],
    null,
    ['Module de torsion de Bredt', 'Wt', v['Wt'], 'mm3', 0],
    ['Contrainte de torsion', 'tau,t,Ed', v['tautEd'], 'MPa'],
    ['Moment resistant de torsion', 'T,Rd', v['TRd'], 'kN.m'],
    ['Cisaillement reduit par la torsion', 'Vpl,T,Rd', v['VplTRd'], 'kN'],
    ["Cisaillement d'effort tranchant", 'tau,V,Ed', v['tauVEd'], 'MPa'],
    ['Cisaillement total', 'tau,Ed', v['tauEd'], 'MPa'],
    ['Contrainte normale elastique', 'sigma,x,Ed', v['sigxEd'], 'MPa'],
    ['Rotation de torsion sur L', 'theta', v['theta'], 'degres', 3],
    null,
    ['Charge critique y / z', 'Ncr', `${fmt(v['Ncry'], 1)} / ${fmt(v['Ncrz'], 1)}`, 'kN'],
    ['Elancement reduit y / z', 'lambda,bar', `${fmt(v['lamy'], 3)} / ${fmt(v['lamz'], 3)}`, '-'],
    ['Courbe de flambement', '-', v['courbe'], '-'],
    ['Coefficient chi,y / chi,z', 'chi', `${fmt(v['chiY'], 3)} / ${fmt(v['chiZ'], 3)}`, '-'],
    ['Resistance flambement y / z', 'Nb,Rd', `${fmt(v['NbyRd'], 1)} / ${fmt(v['NbzRd'], 1)}`, 'kN'],
    null,
    ['Moment critique de deversement', 'Mcr', v['Mcr'], 'kN.m'],
    ['Coefficient de deversement', 'chi,LT', v['chiLT'], '-', 3],
    ['Moment resistant au deversement', 'Mb,Rd', v['MbRd'], 'kN.m'],
    ["Facteurs d'interaction", 'kyy / kzz', `${fmt(v['kyy'], 3)} / ${fmt(v['kzz'], 3)}`, '-'],
    ["Facteurs d'interaction", 'kyz / kzy', `${fmt(v['kyz'], 3)} / ${fmt(v['kzy'], 3)}`, '-'],
  ]);
}

/** Timber. Oracle lines 995–1029: 26 rows and 4 separators. */
export function detailsBois(v: Values): string {
  return tableVals([
    ['Aire de section', 'A', v['A'], 'mm2', 0],
    ['Modules de flexion', 'Wy / Wz', `${fmt(v['Wy'], 0)} / ${fmt(v['Wz'], 0)}`, 'mm3'],
    ['Inerties', 'Iy / Iz', `${fmt(v['Iy'], 0)} / ${fmt(v['Iz'], 0)}`, 'mm4'],
    ['Rayons de giration', 'iy / iz', `${fmt(v['iy'], 1)} / ${fmt(v['iz'], 1)}`, 'mm'],
    null,
    ['Coefficient de duree et humidite', 'kmod', v['kmod'], '-'],
    ['Coefficient de fluage', 'kdef', v['kdef'], '-'],
    ['Coefficient partiel', 'gamma,M', v['gammaM'], '-'],
    ["Effet d'echelle y / z", 'kh', `${fmt(v['khy'], 3)} / ${fmt(v['khz'], 3)}`, '-'],
    null,
    ['Flexion axe fort', 'fm,y,d', v['fmyd'], 'MPa'],
    ['Flexion axe faible', 'fm,z,d', v['fmzd'], 'MPa'],
    ['Traction axiale', 'ft,0,d', v['ft0d'], 'MPa'],
    ['Compression axiale', 'fc,0,d', v['fc0d'], 'MPa'],
    ['Compression transversale', 'fc,90,d', v['fc90d'], 'MPa'],
    ['Cisaillement', 'fv,d', v['fvd'], 'MPa'],
    null,
    ['Compression axiale', 'sigma,c,0,d', v['sc0d'], 'MPa'],
    ['Traction axiale', 'sigma,t,0,d', v['st0d'], 'MPa'],
    ['Flexion axe fort', 'sigma,m,y,d', v['smyd'], 'MPa'],
    ['Flexion axe faible', 'sigma,m,z,d', v['smzd'], 'MPa'],
    ['Cisaillement', 'tau,d', v['taud'], 'MPa'],
    ['Compression transversale', 'sigma,c,90,d', v['sc90d'], 'MPa'],
    null,
    ['Elancements mecaniques', 'lambda,y / z', `${fmt(v['lamY'], 1)} / ${fmt(v['lamZ'], 1)}`, '-'],
    ['Elancements relatifs', 'lambda,rel', `${fmt(v['lrelY'], 3)} / ${fmt(v['lrelZ'], 3)}`, '-'],
    ['Coefficients de flambement', 'kc,y / kc,z', `${fmt(v['kcy'], 3)} / ${fmt(v['kcz'], 3)}`, '-'],
    ['Contrainte critique de deversement', 'sigma,m,crit', v['smcrit'], 'MPa'],
    ['Elancement relatif de deversement', 'lambda,rel,m', v['lrelm'], '-', 3],
    ['Coefficient de deversement', 'k,crit', v['kcrit'], '-', 3],
  ]);
}

/** Dispatch by module, as the oracle's ternary chain does at lines 1073 and 1179. */
export function detailsFor(module: 'lamine' | 'creux' | 'bois', values: Values): string {
  if (module === 'lamine') return detailsLamine(values);
  if (module === 'creux') return detailsCreux(values);
  return detailsBois(values);
}
